import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AlertTriangle, Download, ExternalLink } from 'lucide-react'

import { useTenantBranding } from '../../../contexts/TenantContext'

const API_BASE = import.meta.env.VITE_PROJECTS_API_URL || '/api/projects'

/**
 * The customer's copy of a signed field ticket, opened from the link we email them.
 *
 * SHOWS THE DOCUMENT, not a description of it. The first cut of this page rendered a summary card
 * with a download button, which is a worse version of the attachment it replaced: the customer came
 * to see the ticket they signed and had to take a second action to see anything. The PDF is the
 * page.
 *
 * WHY A PAGE AND NOT AN ATTACHMENT. Measured 2026-08-17 against a real Microsoft 365 tenant: the
 * identical message was quarantined with a PDF attached and reached the inbox without one, and a ZIP
 * made no difference. No bounce is raised for a quarantine, so the sender is told "delivered" while
 * the customer has nothing.
 *
 * PUBLIC AND READ-ONLY. No login: the token in the url is the credential, and it is a DIFFERENT
 * column from the approval token, so nobody holding this link can sign anything.
 */
export default function SignedTicketCopy() {
  const { slug, code } = useParams()
  const brand = useTenantBranding()
  const [ticket, setTicket] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // "Email me my documents" rather than a link to the folder. A ticket link gets forwarded,
  // archived and inherited; putting the folder url on this page would turn a link that expires in
  // 90 days into standing access to the client's entire document history for whoever received it.
  // Sending it to the address on the client record means only control of that mailbox grants it.
  const [folderState, setFolderState] = useState(null)   // null | 'sending' | {sent_to} | {error}

  // NO ADDRESS BOX. There was one. This page is reached from a link that is emailed, forwarded
  // freely, sent identically to every extra recipient the office adds by hand, and valid 90 days —
  // so a typed address meant anyone holding one could have the client's ENTIRE document history
  // delivered to a mailbox they chose. It now goes to the contact on the client record and nowhere
  // else. Staff can still send it anywhere from the client page, behind a login.

  // The document endpoint is public and serves `inline`, so it can be framed directly rather than
  // fetched into a blob first — which is what lets the PDF appear without a second click.
  const pdfUrl = `${API_BASE}/client/ticket/${encodeURIComponent(slug)}/${encodeURIComponent(code)}/pdf`

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/client/ticket/${encodeURIComponent(slug)}/${encodeURIComponent(code)}`)
        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          throw new Error(data.detail || 'This link is no longer valid.')
        }
        const data = await res.json()
        if (!cancelled) setTicket(data)
      } catch (e) {
        if (!cancelled) setError(e.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [slug, code])

  const sendFolderLink = async () => {
    setFolderState('sending')
    try {
      const res = await fetch(
        `${API_BASE}/client/ticket/${encodeURIComponent(slug)}/${encodeURIComponent(code)}/send-folder-link`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}) })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body.detail || 'We could not send that just now.')
      setFolderState({ sent_to: body.sent_to })
    } catch (e) {
      setFolderState({ error: e.message })
    }
  }

  if (loading) {
    return (
      <Shell brand={brand}>
        <p className="p-6 text-sm text-gray-500">Loading your ticket…</p>
      </Shell>
    )
  }

  if (error) {
    return (
      <Shell brand={brand}>
        <div className="m-4 flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-medium">{error}</p>
            <p className="mt-1">
              Contact the company that sent this and they can issue a new copy.
            </p>
          </div>
        </div>
      </Shell>
    )
  }

  return (
    <Shell brand={brand}>
      {/* A single compact line. Everything else worth knowing is ON the document below. */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3">
        <div className="min-w-0">
          <h1 className="truncate text-sm font-semibold text-gray-900">
            Field Ticket {ticket.ticket_number}
          </h1>
          <p className="truncate text-xs text-gray-500">
            {[ticket.project_name, fmtDate(ticket.report_date)].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* A plain anchor, not a scripted download: it works when the framed viewer does not, and
              on a phone it hands the file to whatever the customer normally uses to read PDFs. */}
          <a
            href={pdfUrl}
            download={ticket.download_name || 'field-ticket.pdf'}
            className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Download
          </a>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
            Open
          </a>
        </div>
      </div>

      {/* THE DOCUMENT IS THE PAGE. `object` rather than `iframe` because it degrades honestly: a
          browser with no PDF viewer (older mobile Safari, some in-app webviews) renders the children
          below instead of a blank grey rectangle, which is what an iframe leaves behind. */}
      <object data={pdfUrl} type="application/pdf" className="h-[calc(100vh-8.5rem)] w-full bg-gray-100">
        <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
          <p className="text-sm text-gray-700">
            Your browser can’t display the ticket here.
          </p>
          <a
            href={pdfUrl}
            className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Open the PDF
          </a>
        </div>
      </object>

      {/* Below the document, because the document is why they came. Offers the folder without
          putting its url on a page reachable by anyone the ticket email was forwarded to. */}
      <div className="border-t border-gray-200 bg-white px-4 py-3 text-sm">
        {folderState && folderState.sent_to ? (
          <p className="text-gray-700">
            Sent — check <span className="font-medium">{folderState.sent_to}</span> for a link to all
            your documents.
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={sendFolderLink}
              disabled={folderState === 'sending'}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {folderState === 'sending' ? 'Sending…' : 'Email me all my documents'}
            </button>
            <span className="text-xs text-gray-500">
              We’ll send a lasting link to the address we have on file for you.
            </span>
          </div>
        )}
        {folderState && folderState.error && (
          <p className="mt-2 text-xs text-red-700">{folderState.error}</p>
        )}
      </div>
    </Shell>
  )
}

function Shell({ brand, children }) {
  return (
    <div className="min-h-screen bg-gray-50">
      {brand?.company_name && (
        <div className="border-b border-gray-200 bg-white px-4 py-2">
          <p className="text-sm font-medium text-gray-600">{brand.company_name}</p>
        </div>
      )}
      {children}
    </div>
  )
}

const fmtDate = (v) => (v ? new Date(`${v}T00:00:00`).toLocaleDateString() : '')
